import { defineStore } from "pinia";
import { createBaseStoreRefactor } from "./baseStoreRefactor";
import { useAccountsStoreRefactor } from "./accountsStoreRefactor";

/**
 * Account Search Store
 *
 * This store is specifically designed for search functionality and is separate from
 * the main accounts store. It provides:
 *
 * 1. Search-only context (no caching, infinite loading support)
 * 2. Account-specific getters for search results
 * 3. Clean reactive bridge to main store for real-time updates
 *
 * REAL-TIME UPDATES ARCHITECTURE:
 * ================================
 *
 * Problem: Search stores don't have ActionCable subscriptions (to avoid conflicts),
 * but users expect to see new items appear in search results when they're created
 * via ActionCable in other parts of the app.
 *
 * Solution: Clean reactive bridge using Pinia's built-in reactivity:
 *
 * 1. When search() is called, we subscribe to main store data changes via $subscribe
 * 2. When main store data changes, we intelligently sync matching items to search results
 * 3. Items are filtered based on current search criteria (name, description, etc.)
 * 4. Real-time updates are applied without disrupting search context
 *
 * Flow:
 * ActionCable → Main Store → Pinia $subscribe → Search Store (filtered sync)
 *
 * Benefits:
 * - No method overrides or coupling between stores
 * - Uses Pinia's native reactivity system
 * - Proper cleanup and memory management
 * - Intelligent filtering preserves search context
 */

// Create the base store with accounts configuration
const accountsBaseStore = createBaseStoreRefactor({
  endpoint: "/api/v1/accounts",
  name: "AccountSearchStoreRefactor",
  objectName: "Account", // Auto-generates CRUD success messages
  enableCache: true, // Enable cache for better UX - show cached results while fetching fresh data
  actionCableChannel: null, // Disable ActionCable for search store (main store handles real-time updates)
  infiniteLoading: true, // Support pagination for account lists
});

export const useAccountSearchStore = defineStore("accountSearch", {
  ...accountsBaseStore,

  state: () => {
    const baseState = accountsBaseStore.state();
    return {
      ...baseState,
      currentSearchParams: null, // Track current search parameters
      mainStoreUnsubscribe: null, // Track cleanup function for main store subscription
      actionCableSyncInProgress: false, // Flag to prevent infinite loading during ActionCable updates
      _syncInProgress: false, // Guard against re-entrant sync operations
      _isActivelyFetching: false, // 🔒 Track when search store is fetching its own data
      _lastSearchRequestVersion: 0, // Track the request version for our own searches
    };
  },

  getters: {
    ...accountsBaseStore.getters,

    // 🔒 NEW GETTER: Safe pagination check that considers ActionCable sync state
    shouldLoadMore: (state) => {
      // Don't load more during ActionCable sync to prevent infinite loading triggers
      if (state.actionCableSyncInProgress) {
        return false;
      }
      // Otherwise check if we actually have more data to load
      return state.hasMoreData && !state.loading;
    },

    // Convenience aliases for search context - conditionally sorted based on search mode
    // NOTE: Returns the data array directly to avoid infinite re-render loops
    // Sorting is done once when data changes, not on every getter access
    searchResults: (state) => {
      // Return data directly - sorting happens in syncWithMainStore
      return state.data;
    },
    hasSearchResults: (state) => state.data.length > 0,

    // Check if current search is using geolocation
    isGeolocationSearch: (state) => {
      return (
        state.currentSearchParams &&
        (state.currentSearchParams.search_lat ||
          state.currentSearchParams.search_lng)
      );
    },
  },

  actions: {
    ...accountsBaseStore.actions,

    // Override search to track search parameters and set up reactive bridge
    async search(searchParams = {}, append = false) {
      // Store current search parameters for real-time filtering
      this.currentSearchParams = searchParams.q || searchParams;

      // Subscribe to main store updates if not already subscribed
      if (!this.mainStoreUnsubscribe) {
        this.subscribeToMainStore();
      }

      // 🔒 RACE CONDITION FIX: Mark that we're actively fetching our own data
      this._isActivelyFetching = true;
      this._lastSearchRequestVersion = this.requestVersion + 1; // The version that will be used

      try {
        // Call the original search method (API call with backend filtering)
        const result = await accountsBaseStore.actions.search.call(
          this,
          searchParams,
          append,
        );
        return result;
      } finally {
        // 🔒 Only clear the flag if this was the latest search
        // (prevents clearing if a newer search started while we were waiting)
        if (this._lastSearchRequestVersion === this.requestVersion) {
          this._isActivelyFetching = false;
        }
      }
    },

    // ✨ CLEAN: Use Pinia's built-in reactivity instead of method override
    subscribeToMainStore() {
      if (this.mainStoreUnsubscribe) return;

      const mainStore = useAccountsStoreRefactor();
      const searchStore = this;

      // Use Pinia's $subscribe to watch main store data changes
      this.mainStoreUnsubscribe = mainStore.$subscribe(
        (mutation, state) => {
          // 🔄 ALWAYS sync - even with no search params (show all accounts)
          // No search params means "show all" so ActionCable updates should be reflected
          searchStore.syncWithMainStore(state.data);
        },
        { detached: true }, // Continue watching even if search store is destroyed
      );
    },

    // Sync search store with main store based on current criteria
    // This handles ActionCable updates - new/updated accounts from real-time sync
    syncWithMainStore(mainStoreData) {
      // 🔒 GUARD: Prevent re-entrant sync which can cause infinite loops
      if (this._syncInProgress) {
        console.log("AccountSearchStore: Skipping re-entrant sync");
        return;
      }

      // 🔒 RACE CONDITION FIX: Don't sync while we're actively fetching our own data
      // This prevents dashboard loading from interfering with the account list
      if (this._isActivelyFetching) {
        console.log(
          "AccountSearchStore: Skipping sync while actively fetching - preventing race condition",
        );
        return;
      }

      // 🔒 RACE CONDITION FIX: Don't sync if we have pending requests
      // This means we're waiting for our own API response
      if (this.loading && this.pendingRequests.size > 0) {
        console.log(
          "AccountSearchStore: Skipping sync during pending requests - waiting for our own response",
        );
        return;
      }

      if (!Array.isArray(mainStoreData)) return;

      // SMART SYNC: Don't clear search store if main store is empty and we have data
      // This prevents the race condition where App.vue initialization clears search results
      if (mainStoreData.length === 0 && this.data.length > 0) {
        console.log(
          "AccountSearchStore: Skipping sync with empty main store - preserving search results",
        );
        return;
      }

      // GUARD: Ensure search store data is properly initialized
      if (!Array.isArray(this.data)) {
        return;
      }

      // 🔒 CRITICAL FIX: Set flags to prevent infinite loading and re-entrancy
      this._syncInProgress = true;
      this.actionCableSyncInProgress = true;

      try {
        // Build map of current search results for efficient lookup
        const currentById = new Map(this.data.map((a) => [a.id, a]));
        // const mainStoreById = new Map(mainStoreData.map((a) => [a.id, a]));

        // Track which accounts we need to add to the front (new via ActionCable)
        const newAccountsToAdd = [];
        // Track which accounts need updating in place
        const updatedAccounts = new Map();

        // Check each main store account
        mainStoreData.forEach((account) => {
          const existingInSearch = currentById.get(account.id);

          // Check if this account matches current search criteria
          const matchesCriteria = this.currentSearchParams
            ? this.accountMatchesCriteria(account, this.currentSearchParams)
            : true; // No filters = show all

          if (existingInSearch) {
            // Account already in search results
            if (!matchesCriteria) {
              // No longer matches criteria - will be removed
              updatedAccounts.set(account.id, null); // Mark for removal
            } else if (
              existingInSearch.last_modified !== account.last_modified
            ) {
              // Data changed - update in place
              updatedAccounts.set(account.id, { ...account });
            }
            // Otherwise: no change needed, keep existing
          }

          // 🔒 DISABLED: Do not add new accounts from main store sync.
          // Only update existing records to prevent disrupting the current search/pagination state.
          // If we add new items here, it can mess up the infinite loading offset or show items
          // that wouldn't naturally be here yet.
          /*
          else if (matchesCriteria) {
            // NEW account that matches criteria - add to front
            newAccountsToAdd.push({ ...account });
          }
          */
          // Else: doesn't match criteria and not in results - ignore
        });

        // 🔒 CRITICAL FIX: Do NOT remove items just because they are missing from main store
        // Main store and Search store have independent data sets.
        // Main store is NOT a master replica of all data.
        // Only updates and additions should be synced. Deletions must be handled differently
        // (e.g. via explicit events) or ignored (user can refresh).
        const removedIds = [];
        /* 
        // OLD LOGIC - CAUSED DATA LOSS AND FREEZES
        this.data.forEach((account) => {
          if (!mainStoreById.has(account.id)) {
            removedIds.push(account.id);
          }
        });
        */

        // Apply updates efficiently in a single pass
        if (
          newAccountsToAdd.length > 0 ||
          updatedAccounts.size > 0 ||
          removedIds.length > 0
        ) {
          // Build new array: new accounts first, then existing (with updates applied)
          const newData = [
            ...newAccountsToAdd, // New accounts at the front (most recent)
            ...this.data
              .filter((a) => !removedIds.includes(a.id)) // Remove deleted
              .filter((a) => updatedAccounts.get(a.id) !== null) // Remove those that no longer match
              .map((a) => updatedAccounts.get(a.id) || a), // Apply updates or keep original
          ];

          this.data = newData;

          if (newAccountsToAdd.length > 0) {
            console.log(
              `AccountSearchStore: Added ${newAccountsToAdd.length} new account(s) to front of list`,
            );
          }
        }
      } finally {
        // 🔒 Clear the flags after sync completes
        this._syncInProgress = false;
        // Use setTimeout to ensure V3InfiniteLoading sees the flag during the sync cycle
        setTimeout(() => {
          this.actionCableSyncInProgress = false;
        }, 50);
      }
    },

    // Check if account matches current search criteria and update accordingly
    upsertIfMatches(account, prepend = false) {
      if (!this.currentSearchParams) return;

      const matches = this.accountMatchesCriteria(
        account,
        this.currentSearchParams,
      );

      if (matches) {
        // Add/update account in search results
        const existingIndex = this.data.findIndex((a) => a.id === account.id);

        if (existingIndex !== -1) {
          // Update existing
          this.data[existingIndex] = { ...account };
        } else {
          // Add new item
          if (prepend) {
            this.data.unshift({ ...account });
          } else {
            this.data.push({ ...account });
          }
        }
      } else {
        // Remove from search results if it no longer matches
        this.data = this.data.filter((a) => a.id !== account.id);
      }
    },

    // Filter accounts from main store based on search criteria
    getAccountsMatchingCriteria(accounts, searchParams) {
      if (!searchParams || !Array.isArray(accounts)) return [];

      return accounts.filter((account) =>
        this.accountMatchesCriteria(account, searchParams),
      );
    },

    // Check if individual account matches search criteria
    accountMatchesCriteria(account, searchParams) {
      if (!searchParams || !account) return false;

      // Extract the actual search string from searchParams
      let searchString = "";
      if (typeof searchParams === "string") {
        searchString = searchParams;
      } else if (typeof searchParams === "object") {
        // Handle different search parameter formats
        searchString =
          searchParams.name_cont || searchParams.q || searchParams.search || "";

        // 🔍 For complex search params (like Ransack), check if any criteria is active
        if (!searchString && typeof searchParams === "object") {
          const hasActiveCriteria = Object.entries(searchParams).some(
            ([, value]) => {
              return value && value.toString().trim() !== "";
            },
          );

          // If no active search criteria, ALL accounts match (show all)
          if (!hasActiveCriteria) return true;
        }
      }

      // 🔄 CRITICAL FIX: If no search string but we have searchParams object,
      // it means "show all" - so return true
      if (!searchString) return true;

      const searchLower = searchString.toLowerCase();

      // Check account name
      if (account.name && account.name.toLowerCase().includes(searchLower)) {
        return true;
      }

      // Check account description
      if (
        account.description &&
        account.description.toLowerCase().includes(searchLower)
      ) {
        return true;
      }

      // Check account notes
      if (account.notes && account.notes.toLowerCase().includes(searchLower)) {
        return true;
      }

      // Check company name
      if (
        account.company_name &&
        account.company_name.toLowerCase().includes(searchLower)
      ) {
        return true;
      }

      // Check primary location display
      if (
        account.primary_location_display &&
        account.primary_location_display.toLowerCase().includes(searchLower)
      ) {
        return true;
      }

      // Check regional binding (fallback location)
      if (
        account.regional_binding &&
        account.regional_binding.toLowerCase().includes(searchLower)
      ) {
        return true;
      }

      // Check all linked locations with structured address data
      if (account.linked_locations && Array.isArray(account.linked_locations)) {
        const locationMatch = account.linked_locations.some((ll) => {
          const location = ll.location;
          if (!location) return false;

          return (
            (location.name &&
              location.name.toLowerCase().includes(searchLower)) ||
            (location.display_name &&
              location.display_name.toLowerCase().includes(searchLower)) ||
            (location.formatted_address &&
              location.formatted_address.toLowerCase().includes(searchLower)) ||
            (location.street_address &&
              location.street_address.toLowerCase().includes(searchLower)) ||
            (location.route &&
              location.route.toLowerCase().includes(searchLower)) ||
            (location.locality &&
              location.locality.toLowerCase().includes(searchLower)) ||
            (location.city &&
              location.city.toLowerCase().includes(searchLower)) ||
            (location.postal_code &&
              location.postal_code.toLowerCase().includes(searchLower)) ||
            (location.administrative_area_level_1 &&
              location.administrative_area_level_1
                .toLowerCase()
                .includes(searchLower)) ||
            (location.country &&
              location.country.toLowerCase().includes(searchLower))
          );
        });

        if (locationMatch) return true;
      }

      return false;
    },

    // Override clear to reset search parameters and cleanup subscription
    clear() {
      this.currentSearchParams = null;
      this._isActivelyFetching = false;
      this._lastSearchRequestVersion = 0;

      // Cleanup main store subscription
      if (this.mainStoreUnsubscribe) {
        this.mainStoreUnsubscribe();
        this.mainStoreUnsubscribe = null;
      }

      return accountsBaseStore.actions.clear.call(this);
    },

    // Cleanup method for when store is destroyed
    $dispose() {
      if (this.mainStoreUnsubscribe) {
        this.mainStoreUnsubscribe();
        this.mainStoreUnsubscribe = null;
      }
    },
  },
});
